import React, { useState, ReactNode } from 'react';
import { Menu, MenuItem, alpha } from '@mui/material';
import styled from '@emotion/styled';
import { bluePlanetTheme } from 'ui/theme';
import { quarterSpacing, halfSpacing, contentSpacing } from 'ui/theme/themeConstants';
import SortAscIcon from 'ui/elements/icons/SortAscIcon';
import SortDescIcon from 'ui/elements/icons/SortDescIcon';
import FilterIcon2 from 'ui/elements/icons/FilterIcon2';
import { SortDirection } from './useSorting';
import Button from 'ui/elements/buttons/Button';

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${bluePlanetTheme.bluePlanetPalette.grey.light};
`;

const StyledMenuItem = styled(MenuItem)`
  padding: ${halfSpacing} ${contentSpacing};

  &:hover {
    background-color: ${alpha(bluePlanetTheme.bluePlanetPalette.grey.light, 0.5)};
  }

  svg {
    margin-right: ${quarterSpacing};
  }
`;

const ButtonContainer = styled.div`
  padding: 0 ${halfSpacing};
  margin-bottom: ${halfSpacing};
`;

export interface FilterConfig {
  type: 'text' | 'single-select' | 'multi-select';
  label?: string;
  currentValue?: any;
  options?: Array<{ id: string; label: string }>;
  onChange: (value: any) => void;
  renderContent?: () => ReactNode;
  preventClose?: boolean;
}

interface HeaderPopMenuProps {
  children: ReactNode;
  isSortable?: boolean;
  currentSortDirection?: SortDirection;
  onSortChange?: (direction: SortDirection) => void;
  filter?: FilterConfig;
  anchorEl?: HTMLElement | null;
  onClose?: () => void;
}

export default function HeaderPopMenu({
  children,
  isSortable,
  currentSortDirection,
  onSortChange,
  filter,
  anchorEl,
  onClose,
}: HeaderPopMenuProps) {
  const isOpen = Boolean(anchorEl);

  // Track local filter state - only apply on menu close
  const [localFilterValue, setLocalFilterValue] = useState<any>(filter?.currentValue);

  // Update local state when prop changes (e.g., from external clear)
  React.useEffect(() => {
    setLocalFilterValue(filter?.currentValue);
  }, [filter?.currentValue]);

  // Reset local state when menu opens
  React.useEffect(() => {
    if (isOpen) {
      setLocalFilterValue(filter?.currentValue);
    }
  }, [isOpen, filter?.currentValue]);

  const handleClose = () => {
    // Apply filter changes on close
    if (filter && localFilterValue !== filter.currentValue) {
      filter.onChange(localFilterValue);
    }
    onClose?.();
  };

  const handleSortClick = (direction: SortDirection) => {
    if (onSortChange) {
      onSortChange(direction);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && filter?.type === 'multi-select') {
      e.preventDefault();
      handleClose();
    }
  };

  const hasContent = isSortable || filter;

  if (!hasContent) {
    return <span>{children}</span>;
  }

  return (
    <>
      {children}
      <Menu
        sx={{
          '& .MuiList-root': {
            padding: 0,
          },
        }}
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        onKeyDown={handleKeyDown}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            style: {
              minWidth: '200px',
            },
          },
        }}
      >
        {filter && (
          <>
            {filter.renderContent ? (
              <>
                {/* Pass local state and setter to filter components */}
                {React.cloneElement(filter.renderContent() as React.ReactElement, {
                  value: localFilterValue,
                  selectedIds: localFilterValue, // For multi-select components
                  onChange: setLocalFilterValue,
                  onClose: handleClose, // For text filter to close menu on apply
                })}
                {filter.type === 'multi-select' && (
                  <ButtonContainer>
                    <Button kind="tertiary" onClick={handleClose} style={{ width: '100%' }}>
                      View results
                    </Button>
                  </ButtonContainer>
                )}
              </>
            ) : filter.type === 'single-select' && filter.options ? (
              filter.options.map(option => (
                <StyledMenuItem
                  key={option.id}
                  selected={localFilterValue === option.id}
                  onClick={() => {
                    const newValue = localFilterValue === option.id ? undefined : option.id;
                    setLocalFilterValue(newValue);
                    // For single-select, close immediately after selection
                    filter.onChange(newValue);
                    handleClose();
                  }}
                >
                  <FilterIcon2 fontSize="small" />
                  {option.label}
                </StyledMenuItem>
              ))
            ) : null}
            {isSortable && <Divider />}
          </>
        )}

        {isSortable && (
          <>
            <StyledMenuItem selected={currentSortDirection === 'asc'} onClick={() => handleSortClick('asc')}>
              <SortAscIcon fontSize="small" />
              Sort ascending
            </StyledMenuItem>
            <StyledMenuItem selected={currentSortDirection === 'desc'} onClick={() => handleSortClick('desc')}>
              <SortDescIcon fontSize="small" />
              Sort descending
            </StyledMenuItem>
          </>
        )}
      </Menu>
    </>
  );
}
